import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { assignmentAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorHandler';

export const fetchAssignments = createAsyncThunk('assignments/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const response = await assignmentAPI.fetchAssignments(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch assignments'));
  }
});

export const fetchAssignment = createAsyncThunk('assignments/fetchOne', async (id, { rejectWithValue }) => {
  try {
    const response = await assignmentAPI.fetchAssignment(id);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch assignment'));
  }
});

export const createAssignment = createAsyncThunk('assignments/create', async (data, { rejectWithValue }) => {
  try {
    const response = await assignmentAPI.createAssignment(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create assignment'));
  }
});

export const updateAssignment = createAsyncThunk('assignments/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await assignmentAPI.updateAssignment(id, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to update assignment'));
  }
});

export const deleteAssignment = createAsyncThunk('assignments/delete', async (id, { rejectWithValue }) => {
  try {
    await assignmentAPI.deleteAssignment(id);
    return id;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to delete assignment'));
  }
});

export const updateAssignmentStatus = createAsyncThunk('assignments/updateStatus', async ({ id, status }, { rejectWithValue }) => {
  try {
    const response = await assignmentAPI.updateAssignmentStatus(id, { status });
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to update status'));
  }
});

export const fetchAssignmentSubmissions = createAsyncThunk('assignments/fetchSubmissions', async (id, { rejectWithValue }) => {
  try {
    const response = await assignmentAPI.getAssignmentSubmissions(id);
    return { assignmentId: id, submissions: response.data };
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch submissions'));
  }
});

export const gradeSubmission = createAsyncThunk('assignments/gradeSubmission', async ({ submissionId, data }, { rejectWithValue }) => {
  try {
    const response = await assignmentAPI.gradeSubmission(submissionId, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to grade submission'));
  }
});

const initialState = {
  assignments: [],
  currentAssignment: null,
  submissions: [],
  loading: false,
  error: null,
};

const assignmentSlice = createSlice({
  name: 'assignments',
  initialState,
  reducers: {
    clearAssignmentError: (state) => { state.error = null; },
    clearCurrentAssignment: (state) => { state.currentAssignment = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssignments.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAssignments.fulfilled, (state, action) => { state.loading = false; state.assignments = action.payload.data || action.payload; })
      .addCase(fetchAssignments.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(fetchAssignment.fulfilled, (state, action) => { state.currentAssignment = action.payload; })
      .addCase(createAssignment.fulfilled, (state, action) => { state.assignments.unshift(action.payload); })
      .addCase(updateAssignment.fulfilled, (state, action) => {
        state.assignments = state.assignments.map((a) => a.id === action.payload.id ? action.payload : a);
        state.currentAssignment = action.payload;
      })
      .addCase(deleteAssignment.fulfilled, (state, action) => {
        state.assignments = state.assignments.filter((a) => a.id !== action.payload);
      })
      .addCase(updateAssignmentStatus.fulfilled, (state, action) => {
        state.assignments = state.assignments.map((a) => a.id === action.payload.id ? action.payload : a);
        state.currentAssignment = action.payload;
      })
      .addCase(fetchAssignmentSubmissions.fulfilled, (state, action) => { state.submissions = action.payload.submissions || []; })
      .addCase(gradeSubmission.fulfilled, (state, action) => {
        state.submissions = state.submissions.map((s) => s.id === action.payload.id ? action.payload : s);
      });
  },
});

export const { clearAssignmentError, clearCurrentAssignment } = assignmentSlice.actions;
export default assignmentSlice.reducer;
